/**
 * useSpeechRecognition — thin wrapper around the Web Speech API.
 *
 * Returns a mic button state (listening / not listening) and a toggle
 * function. Recognised words are appended to `onResult` as they arrive
 * (interim results) and committed on `onFinalResult`.
 *
 * Browser support: Chrome, Edge, Safari (webkit prefix). Firefox has
 * no implementation — the hook reports `supported = false` and the
 * caller should hide the mic button.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

// Minimal type declarations for the Web Speech API — the TS lib doesn't
// include these yet.
interface SpeechRecognitionEvent extends Event {
  readonly resultIndex: number
  readonly results: SpeechRecognitionResultList
}

interface SpeechRecognitionResultList {
  readonly length: number
  item(index: number): SpeechRecognitionResult
  [index: number]: SpeechRecognitionResult
}

interface SpeechRecognitionResult {
  readonly length: number
  item(index: number): SpeechRecognitionAlternative
  [index: number]: SpeechRecognitionAlternative
  readonly isFinal: boolean
}

interface SpeechRecognitionAlternative {
  readonly transcript: string
  readonly confidence: number
}

interface SpeechRecognition extends EventTarget {
  lang: string
  continuous: boolean
  interimResults: boolean
  maxAlternatives: number
  start(): void
  stop(): void
  abort(): void
  onresult: ((event: SpeechRecognitionEvent) => void) | null
  onerror: ((event: Event) => void) | null
  onend: (() => void) | null
}

interface SpeechRecognitionStatic {
  new (): SpeechRecognition
}

function getSpeechRecognition(): SpeechRecognitionStatic | null {
  if (typeof window === 'undefined') return null
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionStatic
    webkitSpeechRecognition?: SpeechRecognitionStatic
  }
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
}

export interface UseSpeechRecognitionOptions {
  lang?: string
  onResult?: (transcript: string, isFinal: boolean) => void
}

export function useSpeechRecognition(opts: UseSpeechRecognitionOptions = {}) {
  const { lang = 'en-US', onResult } = opts
  const [listening, setListening] = useState(false)
  const [supported] = useState(() => getSpeechRecognition() !== null)
  const recognitionRef = useRef<SpeechRecognition | null>(null)
  const onResultRef = useRef(onResult)

  // Keep the callback ref current without re-creating the recognition instance.
  useEffect(() => {
    onResultRef.current = onResult
  }, [onResult])

  const stop = useCallback(() => {
    recognitionRef.current?.stop()
    setListening(false)
  }, [])

  const start = useCallback(() => {
    const SR = getSpeechRecognition()
    if (!SR) return

    // Clean up any previous instance.
    recognitionRef.current?.abort()

    const recognition = new SR()
    recognition.lang = lang
    recognition.continuous = true
    recognition.interimResults = true
    recognition.maxAlternatives = 1

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interim = ''
      let final = ''
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results.item(i)
        if (!result) continue
        if (result.isFinal) {
          final += result.item(0)?.transcript ?? ''
        } else {
          interim += result.item(0)?.transcript ?? ''
        }
      }
      if (final && onResultRef.current) {
        onResultRef.current(final.trim(), true)
      } else if (interim && onResultRef.current) {
        onResultRef.current(interim.trim(), false)
      }
    }

    recognition.onerror = () => {
      setListening(false)
    }

    recognition.onend = () => {
      setListening(false)
    }

    recognitionRef.current = recognition
    recognition.start()
    setListening(true)
  }, [lang])

  const toggle = useCallback(() => {
    if (listening) {
      stop()
    } else {
      start()
    }
  }, [listening, start, stop])

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      recognitionRef.current?.abort()
    }
  }, [])

  return { listening, supported, start, stop, toggle }
}
